import { type CSSProperties, memo, type ReactNode, useMemo } from 'react';

import { cn } from '@air/utils-tailwind';

const QUICK_REPLY_CHIP_SHIMMER_SLOT_SECONDS = 4;

const QUICK_REPLY_CHIP_SHIMMER_PALETTE_CLASSNAMES: Partial<Record<string, string>> = {
  'hero-artist-variants': 'canvas-composer-border-shimmer--smart-resize',
  'hero-bulk-rename': 'canvas-composer-border-shimmer--smart-resize',
  'hero-bulk-tag-metadata': 'canvas-composer-border-shimmer--convert-video',
  'hero-summarize-status': 'canvas-composer-border-shimmer--translate',
};

type QuickReplyChipShimmerStyle = CSSProperties & {
  '--shimmer-pill-delay': string;
  '--shimmer-pill-duration': string;
};

export type WorkspaceAgentQuickReplyChipShimmerProps = {
  children: ReactNode;
  /** Layout classes. Radius is `isRounded`, not this. */
  className?: string;
  index: number;
  /** Clip to `rounded-8` instead of the canvas pill's capsule. */
  isRounded: boolean;
  slotCount: number;
  suggestionId: string;
};

/**
 * Animated border shell for {@link WorkspaceAgentQuickReplyChip}. Pass `isBorderless` on the chip so
 * its stroke does not fight the shimmer ring.
 */
export const WorkspaceAgentQuickReplyChipShimmer = memo(
  ({ children, className, index, isRounded, slotCount, suggestionId }: WorkspaceAgentQuickReplyChipShimmerProps) => {
    const shimmerStyle = useMemo(
      (): QuickReplyChipShimmerStyle => ({
        '--shimmer-pill-delay': `${-index * QUICK_REPLY_CHIP_SHIMMER_SLOT_SECONDS}s`,
        '--shimmer-pill-duration': `${slotCount * QUICK_REPLY_CHIP_SHIMMER_SLOT_SECONDS}s`,
      }),
      [index, slotCount],
    );

    const paletteClassName = QUICK_REPLY_CHIP_SHIMMER_PALETTE_CLASSNAMES[suggestionId];

    return (
      <div
        className={cn(
          'canvas-composer-border-shimmer canvas-composer-border-shimmer--pill rounded-full p-px',
          // biome-ignore lint/plugin: the pill rule sets 9999px; important beats it without clipping the focus ring
          isRounded && '!rounded-8',
          paletteClassName,
          paletteClassName && 'canvas-composer-border-shimmer--chromatic',
          className,
        )}
        style={shimmerStyle}
      >
        <div aria-hidden className="canvas-composer-border-shimmer-clip">
          <div className="canvas-composer-border-spinner" />
        </div>
        {children}
      </div>
    );
  },
);

WorkspaceAgentQuickReplyChipShimmer.displayName = 'WorkspaceAgentQuickReplyChipShimmer';
